




import { Component, ChangeDetectionStrategy, input, output, signal, effect, OnInit, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Correction, CorrectionLayer, CorrectionReportData, isFullCorrection, BasicCorrection, isStandardCorrection, StandardCorrection, IntermediateCorrection, isIntermediateCorrection } from '../../models/correction.model';
import { UpgradeBannerComponent } from '../upgrade-banner/upgrade-banner.component';
import { CorrectionService } from '../../services/correction.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-correction-report',
  standalone: true,
  template: `


<div class="space-y-6 animate-fade-in">

@if(isFullCorrection(report())){
  <!-- ============================================= -->
  <!-- ===== PREMIUM (FULL) REPORT TEMPLATE ====== -->
  <!-- ============================================= -->
  
  <!-- Special banner for Free users getting a Pro trial report -->
  @if (isProReportForFreeUser()) {
    <div class="mb-6 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-custom-blue p-4 rounded-r-lg">
      <h3 class="font-bold text-custom-blue">Esta é uma Amostra do Plano Pro!</h3>
      <p class="text-sm text-gray-700 dark:text-gray-300 mt-1">
        Você recebeu uma análise completa como parte do seu teste. Para continuar recebendo este nível de detalhe em todas as suas futuras correções, considere fazer o <button (click)="viewPlansRequest.emit()" class="font-semibold underline hover:text-blue-700 dark:hover:text-blue-400">upgrade para o plano Pro</button>.
      </p>
    </div>
  }

  <!-- Camada 1: Visão Rápida (Sempre visível) -->
  <div class="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-slate-200/80">
    <div class="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
      <div>
        <h1 class="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Sua Correção Chegou!</h1>
        <p class="text-slate-500 mt-1">{{ reportAsFull.resumoDesempenho }}</p>
      </div>
      <div class="text-center">
        <p class="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Nota Final</p>
        <div 
          class="mt-1 w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-bold ring-4 ring-offset-2"
          [class]="getScoreColor(reportAsFull.notaFinal)">
          {{ reportAsFull.notaFinal.toFixed(1) }}
        </div>
      </div>
    </div>
    <div class="mt-6 text-center">
      <p class="font-semibold px-4 py-2 rounded-full inline-block" [class]="getApprovalClasses(reportAsFull.notaFinal)">
        {{ getApprovalText(reportAsFull.notaFinal) }}
      </p>
    </div>
     <div class="mt-6 border-t pt-6">
       <h3 class="text-lg font-bold text-custom-black mb-4 text-center">Notas por Critério (FCC)</h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          @for (criterion of reportAsFull.criterios; track criterion.nome) {
            <div class="bg-custom-gray p-4 rounded-lg text-center">
              <h4 class="font-semibold text-gray-700">{{ criterion.nome }}</h4>
              <p class="text-xl font-bold text-custom-blue">{{ criterion.nota.toFixed(1) }}</p>
            </div>
          }
        </div>
      </div>
  </div>

  <!-- Camada 2: Análise Gramatical -->
  <details #detailsGrammar class="group bg-white rounded-2xl shadow-card border border-slate-200/80" [open]="isLayerVisible('grammar')" (toggle)="toggleLayer('grammar', detailsGrammar)">
    <summary class="p-6 cursor-pointer flex justify-between items-center">
      <div class="flex items-center">
         <div class="w-11 h-11 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mr-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" /></svg>
          </div>
        <div>
          <h2 class="text-lg font-semibold text-slate-900">Camada 2: Análise Gramatical</h2>
          <p class="text-sm text-gray-500">Veja os erros de português e como corrigi-los.</p>
        </div>
      </div>
      <span class="text-sm font-semibold text-custom-blue group-open:hidden">Revelar Análise</span>
      <svg class="w-6 h-6 transform group-open:rotate-180 transition-transform text-custom-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path></svg>
    </summary>
    <div class="p-6 border-t">
      @for(error of reportAsFull.errosGramaticais; track $index) {
        <div class="p-4 border-l-4 border-yellow-400 bg-yellow-50 rounded-r-lg mb-4">
          <p class="font-semibold text-yellow-800 text-sm mb-1 uppercase">{{error.categoria}}</p>
          <blockquote class="text-gray-600 italic">“{{ error.trechoComErro }}”</blockquote>
          <p class="mt-2 text-sm text-gray-700">{{ error.explicacao }}</p>
          <p class="mt-2 font-semibold text-green-700">Sugestão: <span class="font-normal">{{ error.sugestaoCorrecao }}</span></p>
        </div>
      } @empty {
        <p class="text-center text-gray-600">Nenhum erro gramatical significativo foi encontrado. Parabéns!</p>
      }
    </div>
  </details>
  
  <!-- Camada 3: Análise Estrutural e Cognitiva -->
  <details #detailsStructure class="group bg-white rounded-2xl shadow-card border border-slate-200/80" [open]="isLayerVisible('structure')" (toggle)="toggleLayer('structure', detailsStructure)">
    <summary class="p-6 cursor-pointer flex justify-between items-center">
       <div class="flex items-center">
         <div class="w-11 h-11 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center mr-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z" /></svg>
          </div>
        <div>
          <h2 class="text-lg font-semibold text-slate-900">Camada 3: Análise Estrutural e Cognitiva</h2>
          <p class="text-sm text-gray-500">Entenda como você organiza ideias e argumentos.</p>
        </div>
      </div>
      <span class="text-sm font-semibold text-green-600 group-open:hidden">Revelar Análise</span>
       <svg class="w-6 h-6 transform group-open:rotate-180 transition-transform text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path></svg>
    </summary>
    <div class="p-6 border-t space-y-6">
      @for (item of reportAsFull.diagnosticoCognitivo; track item.nome) {
        <div class="border border-gray-200 rounded-xl p-5">
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center"><div class="w-10 h-10 bg-custom-blue text-white rounded-lg flex items-center justify-center mr-3 flex-shrink-0"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" [attr.d]="getIconForCognitiveCategory(item.nome)"/></svg></div><h3 class="font-bold text-lg text-custom-black">{{ item.nome }}</h3></div><div class="flex items-center"><span class="text-custom-blue font-bold text-lg mr-3">{{ item.nota.toFixed(1) }} / 10</span><div class="w-32 bg-custom-gray rounded-full h-2.5"><div class="bg-custom-blue h-2.5 rounded-full" [style.width.%]="item.nota * 10"></div></div></div>
          </div>
          <p class="text-gray-700 leading-relaxed mb-4">{{ item.descricao }}</p>
          @if(item.recomendacoes && item.recomendacoes.length > 0) {<div class="bg-custom-gray p-4 rounded-lg"><h4 class="font-semibold text-sm text-custom-black mb-2">Recomendações Práticas:</h4><ul class="space-y-2">@for (rec of item.recomendacoes; track rec) {<li class="flex items-start"><svg class="w-4 h-4 text-custom-blue mr-2.5 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-11.25a.75.75 0 0 0-1.5 0v4.59L7.3 9.24a.75.75 0 0 0-1.1 1.02l3.25 3.5a.75.75 0 0 0 1.1 0l3.25-3.5a.75.75 0 0 0-1.1-1.02l-1.95 2.1V6.75Z" clip-rule="evenodd" /></svg><span class="text-sm text-gray-700">{{ rec }}</span></li>}</ul></div>}
        </div>
      }
    </div>
  </details>

  <!-- Camada 4: Sugestão de Reescrita Completa -->
  <details #detailsRewrite class="group bg-white rounded-2xl shadow-card border border-slate-200/80" [open]="isLayerVisible('rewrite')" (toggle)="toggleLayer('rewrite', detailsRewrite)">
    <summary class="p-6 cursor-pointer flex justify-between items-center">
       <div class="flex items-center">
         <div class="w-11 h-11 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center mr-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" /></svg>
          </div>
        <div>
          <h2 class="text-lg font-semibold text-slate-900">Camada 4: Reescrita Completa Sugerida</h2>
          <p class="text-sm text-gray-500">Uma versão aprimorada da sua redação para servir de modelo.</p>
        </div>
      </div>
      <span class="text-sm font-semibold text-purple-600 group-open:hidden">Revelar Análise</span>
       <svg class="w-6 h-6 transform group-open:rotate-180 transition-transform text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path></svg>
    </summary>
    <div class="p-6 border-t">
      <div class="prose prose-sm max-w-none bg-custom-gray p-4 rounded-lg whitespace-pre-wrap">{{ reportAsFull.reescritaCompleta }}</div>
    </div>
  </details>

  <!-- Camada 5: Modo Professor -->
  <details #detailsPedagogy class="group bg-white rounded-2xl shadow-card border border-slate-200/80" [open]="isLayerVisible('pedagogy')" (toggle)="toggleLayer('pedagogy', detailsPedagogy)">
     <summary class="p-6 cursor-pointer flex justify-between items-center">
       <div class="flex items-center">
         <div class="w-11 h-11 bg-red-50 text-red-600 rounded-xl flex items-center justify-center mr-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" /></svg>
          </div>
        <div>
          <h2 class="text-lg font-semibold text-slate-900">Camada 5: Explicação Pedagógica (Modo Professor)</h2>
          <p class="text-sm text-gray-500">Uma conversa para te ajudar a evoluir de verdade.</p>
        </div>
      </div>
      <span class="text-sm font-semibold text-red-600 group-open:hidden">Revelar Análise</span>
       <svg class="w-6 h-6 transform group-open:rotate-180 transition-transform text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path></svg>
    </summary>
    <div class="p-6 border-t space-y-6">
       <p class="text-gray-700 leading-relaxed whitespace-pre-wrap">{{ reportAsFull.explicacaoPedagogica }}</p>
       @if (reportAsFull.fontesParaAprofundamento && reportAsFull.fontesParaAprofundamento.length > 0) {
        <div>
          <h3 class="text-base font-semibold text-slate-900 mb-4">Para Aprofundar seus Conhecimentos</h3>
          <div class="space-y-4">
            @for (source of reportAsFull.fontesParaAprofundamento; track source.titulo) {
              <div class="flex items-start bg-custom-gray p-4 rounded-xl"><div class="w-10 h-10 bg-custom-blue text-white rounded-lg flex items-center justify-center mr-4 flex-shrink-0"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" [attr.d]="getIconForType(source.tipo)"/></svg></div><div><p class="font-bold text-custom-black">{{ source.titulo }}</p><p class="text-sm font-medium text-gray-500 mb-1">{{ source.tipo }}</p><p class="text-sm text-gray-700">{{ source.descricaoOuLink }}</p></div></div>
            }
          </div>
        </div>
       }
    </div>
  </details>

} @else if (isIntermediateCorrection(report())) {
  <!-- ============================================= -->
  <!-- =- INTERMEDIATE REPORT TEMPLATE -= -->
  <!-- ============================================= -->
  <div class="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-slate-200/80">
     <div class="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
      <div>
        <h1 class="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Sua Correção Detalhada</h1>
        <p class="text-slate-500 mt-1">Análise aprofundada dos pontos essenciais da sua redação.</p>
      </div>
      <div class="text-center">
        <p class="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Nota Final</p>
        <div 
          class="mt-1 w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-bold ring-4 ring-offset-2"
          [class]="getScoreColor(reportAsIntermediate.notaFinal)">
          {{ reportAsIntermediate.notaFinal.toFixed(1) }}
        </div>
      </div>
    </div>
    <div class="mt-6 border-t border-slate-100 pt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
      @for (criterion of reportAsIntermediate.criterios; track criterion.nome) {
        <div class="bg-custom-gray p-4 rounded-xl">
          <h4 class="font-semibold text-gray-700 text-center">{{ criterion.nome }}</h4>
          <p class="text-xl font-bold text-custom-blue text-center">{{ criterion.nota.toFixed(1) }}</p>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ criterion.comentario }}</p>
        </div>
      }
    </div>
  </div>

  <div class="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-slate-200/80">
    <h2 class="text-lg font-semibold text-slate-900 mb-4">Diagnóstico Cognitivo e Evolução</h2>
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div class="space-y-4">
        <div class="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
          <h3 class="text-base font-bold text-green-800 mb-1">Principal Ponto Forte</h3>
          <p class="text-sm text-gray-700">{{ reportAsIntermediate.diagnosticoCognitivoSimples.principalPontoForte }}</p>
        </div>
        <div class="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
          <h3 class="text-base font-bold text-red-800 mb-1">Principal Dificuldade</h3>
          <p class="text-sm text-gray-700">{{ reportAsIntermediate.diagnosticoCognitivoSimples.principalDificuldade }}</p>
        </div>
      </div>
      <div>
        <h3 class="text-base font-bold text-gray-800 mb-2 text-center">Evolução Recente (Últimas 5 Notas)</h3>
        <div class="flex justify-around items-end h-32 bg-custom-gray p-4 rounded-lg">
          @for (score of recentScores(); track $index) {
            <div class="flex-1 flex flex-col items-center justify-end mx-1">
              @if(score !== null) {
                <div class="w-full bg-custom-blue rounded-t-md hover:opacity-80 transition-opacity" [style.height.%]="score * 10" [title]="'Nota: ' + score.toFixed(1)"></div>
                <p class="text-xs font-bold mt-1">{{ score.toFixed(1) }}</p>
              } @else {
                <div class="w-full bg-gray-300 rounded-t-md" style="height: 1%"></div>
                 <p class="text-xs text-gray-400 mt-1">-</p>
              }
            </div>
          }
        </div>
      </div>
    </div>
  </div>

  <div class="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-slate-200/80">
    <h2 class="text-lg font-semibold text-slate-900 mb-4">Análise Estrutural Avançada</h2>
    <p class="text-gray-700 leading-relaxed">{{ reportAsIntermediate.analiseEstruturalAvancada }}</p>
  </div>

  <div class="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-slate-200/80">
    <h2 class="text-lg font-semibold text-slate-900 mb-4">Sugestão de Reescrita Parcial</h2>
    @for(item of reportAsIntermediate.reescritaParcial; track $index) {
      <details class="group bg-custom-gray p-4 rounded-lg mb-3">
        <summary class="font-semibold text-gray-800 cursor-pointer">Reescrita para o parágrafo: "{{item.paragrafoOriginal.substring(0, 50)}}..."</summary>
        <div class="mt-3 pt-3 border-t border-gray-200">
           <p class="text-sm text-gray-700 whitespace-pre-wrap">{{item.comentario}}</p>
        </div>
      </details>
    }
  </div>

} @else if (isStandardCorrection(report())) {
  <!-- ============================================= -->
  <!-- =-- STANDARD (BÁSICO) REPORT TEMPLATE --= -->
  <!-- ============================================= -->
  <div class="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-slate-200/80">
    <div class="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
      <div>
        <h1 class="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Sua Correção Chegou</h1>
        <p class="text-slate-500 mt-1">Análise dos pontos essenciais da sua redação.</p>
      </div>
      <div class="text-center">
        <p class="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Nota Final</p>
        <div 
          class="mt-1 w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-bold ring-4 ring-offset-2"
          [class]="getScoreColor(reportAsStandard.notaFinal)">
          {{ reportAsStandard.notaFinal.toFixed(1) }}
        </div>
      </div>
    </div>
    <div class="mt-6 text-center">
      <p class="font-semibold px-4 py-2 rounded-full inline-block" [class]="getApprovalClasses(reportAsStandard.notaFinal)">
        {{ getApprovalText(reportAsStandard.notaFinal) }}
      </p>
    </div>
  </div>

  <div class="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-slate-200/80">
    <h2 class="text-lg font-semibold text-slate-900 mb-4">Análise por Parágrafo</h2>
    <div class="space-y-4">
      @for(item of reportAsStandard.correcaoPorParagrafo; track $index) {
        <details class="group bg-custom-gray p-4 rounded-lg">
          <summary class="font-semibold text-gray-800 cursor-pointer">{{item.paragrafoOriginal.substring(0, 80)}}{{item.paragrafoOriginal.length > 80 ? '...' : ''}}</summary>
          <p class="mt-2 pt-2 border-t border-gray-200 text-sm text-gray-700">{{item.comentario}}</p>
        </details>
      }
    </div>
  </div>

  <div class="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-slate-200/80">
    <h2 class="text-lg font-semibold text-slate-900 mb-4">Análise Gramatical</h2>
    @for(error of reportAsStandard.errosGramaticais; track $index) {
      <div class="p-4 border-l-4 border-yellow-400 bg-yellow-50 rounded-r-lg mb-4">
        <p class="font-semibold text-yellow-800 text-sm mb-1 uppercase">{{error.categoria}}</p>
        <blockquote class="text-gray-600 italic">“{{ error.trechoComErro }}”</blockquote>
        <p class="mt-2 text-sm text-gray-700">{{ error.explicacao }}</p>
        <p class="mt-2 font-semibold text-green-700">Sugestão: <span class="font-normal">{{ error.sugestaoCorrecao }}</span></p>
      </div>
    } @empty {
      <p class="text-center text-gray-600">Nenhum erro gramatical significativo foi encontrado. Parabéns!</p>
    }
  </div>

} @else {
  <!-- ============================================= -->
  <!-- ===== FREE TIER (BASIC) REPORT TEMPLATE ===== -->
  <!-- ============================================= -->
  <div class="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-slate-200/80">
    <div class="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
      <div>
        <h1 class="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Sua Correção Gratuita</h1>
        <p class="text-slate-500 mt-1">Análise rápida com os pontos essenciais para sua evolução.</p>
      </div>
      <div class="text-center">
        <p class="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Nota Final</p>
        <div 
          class="mt-1 w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-bold ring-4 ring-offset-2"
          [class]="getScoreColor(reportAsBasic.notaFinal)">
          {{ reportAsBasic.notaFinal.toFixed(1) }}
        </div>
      </div>
    </div>
     <div class="mt-6 text-center">
      <p class="font-semibold px-4 py-2 rounded-full inline-block" [class]="getApprovalClasses(reportAsBasic.notaFinal)">
        {{ getApprovalText(reportAsBasic.notaFinal) }}
      </p>
    </div>
  </div>

  <div class="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-slate-200/80">
     <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Strength -->
      <div class="bg-green-50 border-l-4 border-green-500 p-5 rounded-r-lg">
        <h3 class="text-lg font-bold text-green-800 mb-2">Ponto Forte Principal</h3>
        <p class="text-gray-700">{{ reportAsBasic.pontoForte }}</p>
      </div>
      <!-- Weakness -->
      <div class="bg-red-50 border-l-4 border-red-500 p-5 rounded-r-lg">
        <h3 class="text-lg font-bold text-red-800 mb-2">Principal Ponto a Melhorar</h3>
        <p class="text-gray-700">{{ reportAsBasic.pontoFraco }}</p>
      </div>
    </div>
  </div>

  <div class="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-slate-200/80">
     <h2 class="text-lg font-semibold text-slate-900 mb-4">Principais Problemas Estruturais</h2>
     <ul class="space-y-3">
        @for(item of reportAsBasic.resumoEstrutural; track $index) {
          <li class="flex items-start"><svg class="w-5 h-5 text-custom-blue mr-3 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.06 0l4-5.5Z" clip-rule="evenodd" /></svg><span class="text-gray-700">{{item}}</span></li>
        }
     </ul>
  </div>

  <div class="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-slate-200/80">
    <h2 class="text-lg font-semibold text-slate-900 mb-4">Principais Erros Gramaticais</h2>
    @for(error of reportAsBasic.errosGramaticais; track $index) {
      <div class="p-4 border-l-4 border-yellow-400 bg-yellow-50 rounded-r-lg mb-4">
        <p class="font-semibold text-yellow-800 text-sm mb-1 uppercase">{{error.categoria}}</p>
        <blockquote class="text-gray-600 italic">“{{ error.trechoComErro }}”</blockquote>
        <p class="mt-2 text-sm text-gray-700">{{ error.explicacao }}</p>
        <p class="mt-2 font-semibold text-green-700">Sugestão: <span class="font-normal">{{ error.sugestaoCorrecao }}</span></p>
      </div>
    } @empty {
      <p class="text-center text-gray-600">Nenhum erro gramatical significativo foi encontrado. Parabéns!</p>
    }
  </div>
}

  <!-- Upgrade Banner -->
  @if (!isFullCorrection(report()) || showUpgradeBannerForTrialUser()) {
    <app-upgrade-banner (upgrade)="viewPlansRequest.emit()"></app-upgrade-banner>
  }

  <!-- Action Buttons -->
  <div class="flex flex-col sm:flex-row items-center justify-between pt-4 gap-4">
    <button (click)="startNew()" class="btn-primary w-full sm:w-auto py-2.5 px-6">
      Enviar Nova Redação
    </button>
    @if(isAuthenticated() && correctionId()) {
      <button (click)="openReportModal()" class="w-full sm:w-auto flex items-center justify-center text-sm text-gray-500 hover:text-red-600 font-semibold transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4 mr-2"><path stroke-linecap="round" stroke-linejoin="round" d="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 15V4.5" /></svg>
        Denunciar correção
      </button>
    }
  </div>
</div>


<!-- Report Correction Modal -->
@if (isReportModalOpen()) {
  <div class="fixed inset-0 bg-black bg-opacity-60 z-40 flex items-center justify-center" (click)="closeReportModal()">
    <div class="bg-white rounded-2xl shadow-card border border-slate-200/80 p-8 w-full max-w-lg" (click)="$event.stopPropagation()">
      @if (reportStatus() === 'success') {
        <div class="text-center">
          <div class="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-8 h-8"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" /></svg>
          </div>
          <h3 class="text-xl font-bold mb-2">Denúncia Enviada</h3>
          <p class="text-gray-600 mb-6">Obrigado! Nossa equipe de moderação irá analisar a correção em breve.</p>
          <button type="button" (click)="closeReportModal()" class="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-custom-blue hover:bg-indigo-700">Fechar</button>
        </div>
      } @else {
        <h3 class="text-xl font-bold mb-4">Denunciar Correção</h3>
        <p class="text-sm text-gray-600 mb-4">Se você acredita que esta correção contém conteúdo ofensivo, inadequado ou incorreto, por favor, nos informe.</p>
        <form #reportForm="ngForm" (ngSubmit)="submitReport()">
          <div>
            <label for="reportReason" class="block text-sm font-medium mb-1">Motivo da denúncia</label>
            <textarea id="reportReason" name="reportReason" rows="4" [(ngModel)]="reportReason" required class="mt-1 block w-full appearance-none rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 px-3 py-2 sm:text-sm" placeholder="Ex: A reescrita sugerida contém informações ofensivas."></textarea>
          </div>
           @if (reportStatus() === 'error') {
            <p class="text-sm text-red-600 mt-2">Ocorreu um erro ao enviar a denúncia. Tente novamente.</p>
          }
          <div class="flex justify-end space-x-3 pt-4 mt-4">
            <button type="button" (click)="closeReportModal()" class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700">Cancelar</button>
            <button type="submit" [disabled]="!reportForm.form.valid || isReporting()" class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-400 flex items-center">
               @if (isReporting()) {
                <div class="loader !w-5 !h-5 !border-2 !border-t-white"></div>
                <span class="ml-2">Enviando...</span>
              } @else {
                 <span>Enviar Denúncia</span>
              }
            </button>
          </div>
        </form>
      }
    </div>
  </div>
}
`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, UpgradeBannerComponent],
})
export class CorrectionReportComponent implements OnInit {
  report = input.required<CorrectionReportData>();
  correctionId = input<number | undefined>();
  history = input<CorrectionReportData[]>([]);
  initialVisibleLayers = input<Set<CorrectionLayer>>(new Set());
  showUpgradeBannerForTrialUser = input<boolean>(false);
  visibleLayersChange = output<Set<CorrectionLayer>>();
  newCorrection = output();
  viewPlansRequest = output<void>();
  
  private correctionService = inject(CorrectionService);
  private authService = inject(AuthService);

  readonly visibleLayers = signal<Set<CorrectionLayer>>(new Set());
  readonly isReportModalOpen = signal(false);
  readonly reportReason = signal('');
  readonly isReporting = signal(false);
  readonly reportStatus = signal<'idle' | 'success' | 'error'>('idle');
  readonly isAuthenticated = this.authService.isAuthenticated;

  readonly isProReportForFreeUser = computed(() => {
    const user = this.authService.currentUser();
    return user.plan === 'gratuito' && isFullCorrection(this.report());
  });

  // Type guards for use in the template
  readonly isFullCorrection = isFullCorrection;
  readonly isStandardCorrection = isStandardCorrection;
  readonly isIntermediateCorrection = isIntermediateCorrection;

  ngOnInit() {
      // Only set initial layers for full reports
      if (this.isFullCorrection(this.report())) {
        this.visibleLayers.set(this.initialVisibleLayers());
      }
  }

  readonly recentScores = computed(() => {
    if (!this.isIntermediateCorrection(this.report())) {
      return [];
    }
    // The history passed in is from *before* the current correction.
    const historyScores = this.history()
      .slice(0, 4) // take latest 4 from history
      .map(c => c.notaFinal)
      .reverse(); // to show oldest to newest
    
    const currentScore = (this.report() as IntermediateCorrection).notaFinal;
    
    const scores = [...historyScores, currentScore];
    // Pad with nulls if fewer than 5 scores to ensure 5 bars are always rendered
    while (scores.length < 5) {
      scores.unshift(null);
    }
    return scores;
  });


  get reportAsFull(): Correction {
    return this.report() as Correction;
  }
  
  get reportAsStandard(): StandardCorrection {
    return this.report() as StandardCorrection;
  }

  get reportAsIntermediate(): IntermediateCorrection {
    return this.report() as IntermediateCorrection;
  }

  get reportAsBasic(): BasicCorrection {
    return this.report() as BasicCorrection;
  }


  isLayerVisible(layer: CorrectionLayer): boolean {
    return this.visibleLayers().has(layer);
  }

  toggleLayer(layer: CorrectionLayer, element: HTMLDetailsElement) {
    this.visibleLayers.update(layers => {
      const newLayers = new Set(layers);
      if (element.open) {
        newLayers.add(layer);
      } else {
        newLayers.delete(layer);
      }
      this.visibleLayersChange.emit(newLayers);
      return newLayers;
    });
  }

  getScoreColor(score: number): string {
    if (score >= 8) return 'bg-green-500';
    if (score >= 6) return 'bg-yellow-500';
    return 'bg-red-500';
  }
  
  getApprovalClasses(score: number): string {
    const isApproved = score >= 6;
    if (this.isFullCorrection(this.report())) {
       return isApproved ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
    }
    // Use a more neutral color for the basic report indication
    return isApproved ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800';
  }

   getApprovalText(score: number): string {
    if (this.isFullCorrection(this.report())) {
      return this.reportAsFull.indicacaoAprovacao;
    }
    return score >= 6 ? 'Nota suficiente para aprovação' : 'Nota abaixo do mínimo para aprovação';
  }

  getIconForCriterion(name: string): string {
    if (name.toLowerCase().includes('conteúdo')) return 'M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z';
    if (name.toLowerCase().includes('estrutura')) return 'M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z';
    if (name.toLowerCase().includes('expressão')) return 'm10.5 21 5.25-11.625M21 21l-5.25-11.625M3.75 5.25c0-1.036.84-1.875 1.875-1.875h.375c1.036 0 1.875.84 1.875 1.875v.375c0 1.036-.84 1.875-1.875 1.875h-.375a1.875 1.875 0 0 1-1.875-1.875v-.375Zm12 0c0-1.036.84-1.875 1.875-1.875h.375c1.036 0 1.875.84 1.875 1.875v.375c0 1.036-.84 1.875-1.875 1.875h-.375a1.875 1.875 0 0 1-1.875-1.875v-.375Z';
    return '';
  }

  getIconForCognitiveCategory(name: string): string {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('organização')) return 'M3.75 9h16.5m-16.5 6.75h16.5';
    if (lowerName.includes('causa e consequência')) return 'M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3';
    if (lowerName.includes('vocabulário')) return 'M10.5 21l-5.25-11.625-5.25 11.625';
    if (lowerName.includes('tese')) return 'M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z';
    if (lowerName.includes('argumentativa')) return 'M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z';
    if (lowerName.includes('aderência') || lowerName.includes('maturidade')) return 'M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z';
    if (lowerName.includes('conclusão')) return 'M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z';
    return 'M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z';
  }

  getIconForType(type: string): string {
    const lowerType = type.toLowerCase();
    if (lowerType.includes('filme')) return 'M6 20.25h12m-7.5-3.75v3.75m3.75-3.75v3.75M17.25 21v-3.75c0-.621-.504-1.125-1.125-1.125H7.875c-.621 0-1.125.504-1.125 1.125V21M3 4.25a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 4.25v8.5A2.25 2.25 0 0 1 18.75 15H5.25A2.25 2.25 0 0 1 3 12.75v-8.5Z';
    if (lowerType.includes('podcast')) return 'M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m12 7.5v-1.5a6 6 0 0 0-6-6v-1.5a6 6 0 0 0-6 6v1.5m12 7.5a6 6 0 0 0-6-6v-1.5a6 6 0 0 0-6 6v1.5m6 7.5v3.75m-3.75-3.75h7.5';
    if (lowerType.includes('notícia')) return 'M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 0 1-2.25 2.25M16.5 7.5V18a2.25 2.25 0 0 0 2.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 0 0 2.25 2.25h13.5M6 7.5h3v3H6v-3Z';
    if (lowerType.includes('livro')) return 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253';
    if (lowerType.includes('artigo')) return 'M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m-1.5 0-3.75 3.75M10.5 2.25h-1.5a2.25 2.25 0 0 0-2.25 2.25v13.5a2.25 2.25 0 0 0 2.25 2.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-13.5a2.25 2.25 0 0 0-2.25-2.25H10.5';
    if (lowerType.includes('vídeo') || lowerType.includes('documentário')) return 'M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z M10.5 9.75L15 12l-4.5 2.25v-4.5Z';
    return 'M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244';
  }

  startNew() {
    this.newCorrection.emit();
  }

  openReportModal() {
    this.reportReason.set('');
    this.reportStatus.set('idle');
    this.isReportModalOpen.set(true);
  }

  closeReportModal() {
    this.isReportModalOpen.set(false);
  }

  async submitReport() {
    if (!this.reportReason() || !this.correctionId()) {
      return;
    }
    this.isReporting.set(true);
    this.reportStatus.set('idle');
    try {
      await this.correctionService.reportCorrection(this.correctionId()!, this.reportReason());
      this.reportStatus.set('success');
    } catch (e) {
      this.reportStatus.set('error');
      console.error('Failed to submit report:', (e as Error).message, e);
    } finally {
      this.isReporting.set(false);
    }
  }
}
